import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-exam-revaluation-rechecking-fees-2026",
  title: "DU Exam Revaluation vs Rechecking Process 2026: Online Form, Fees & Copy Inspection",
  description:
    "Complete 4,000-word step-by-step guide to Delhi University end-semester exam revaluation and rechecking 2026. Official fee structures, SAMARTH portal deadlines, photocopy inspection, moderation committee rules, and grade update timelines.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "college-life",
  tags: ["DU exams", "revaluation", "rechecking", "DU results", "exam branch", "fees", "photocopy-inspection"],
  readMinutes: 20,
  heroEyebrow: "College · Regulations & Grievance Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        Following the declaration of end-semester examination results by the{" "}
        <a href="https://exam.du.ac.in/" target="_blank" rel="noopener noreferrer">
          DU Examination Branch
        </a>
        , students who discover unexpected mark deductions, missing answers in evaluation, or clerical totalling errors can file for official Revaluation, Rechecking, or Answer Script Photocopy Inspection under University of Delhi Ordinance IX rules. This guide covers the complete process — from understanding which option to choose, to filing online, to tracking results.
      </p>

      {/* ─── Section 1: Three Options Compared ─── */}
      <h2>1. Revaluation vs. Rechecking vs. Photocopy Inspection: Complete Comparison</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Feature</th>
              <th className="p-3 border-r border-rule">Rechecking</th>
              <th className="p-3 border-r border-rule">Revaluation</th>
              <th className="p-3">Photocopy Inspection</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Official Fee per Paper</td>
              <td className="p-3 border-r border-rule font-semibold">Rs 750</td>
              <td className="p-3 border-r border-rule font-semibold">Rs 1,000</td>
              <td className="p-3 font-semibold">Rs 500</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Scope of Review</td>
              <td className="p-3 border-r border-rule">Verification that all questions were assessed and marks were correctly totalled. No re-reading of answers.</td>
              <td className="p-3 border-r border-rule">Complete fresh evaluation of the entire answer script by an independent second examiner who has never seen the paper before.</td>
              <td className="p-3">Physical or digital photocopy of the evaluated answer sheet provided to the student for personal review. No re-evaluation.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Can Marks Increase?</td>
              <td className="p-3 border-r border-rule">Yes, if totalling error or unevaluated answers are found.</td>
              <td className="p-3 border-r border-rule">Yes, significantly — the higher of the two evaluations (original and revaluation) is taken as the final mark.</td>
              <td className="p-3">No direct mark change. But you can use the photocopy evidence to file a subsequent revaluation application with stronger grounds.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Can Marks Decrease?</td>
              <td className="p-3 border-r border-rule">No. Rechecking cannot reduce your marks.</td>
              <td className="p-3 border-r border-rule">No. DU rules guarantee that the higher of the two marks (original vs revaluation) is retained.</td>
              <td className="p-3">No. Photocopy inspection has no impact on marks.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Application Deadline</td>
              <td className="p-3 border-r border-rule">Within 15 days of result declaration.</td>
              <td className="p-3 border-r border-rule">Within 15 days of result declaration.</td>
              <td className="p-3">Within 30 days of result declaration.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Result Timeline</td>
              <td className="p-3 border-r border-rule">30 to 45 days after application.</td>
              <td className="p-3 border-r border-rule">45 to 60 days after application.</td>
              <td className="p-3">15 to 30 days for photocopy delivery.</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 2: When to Choose Which ─── */}
      <h2>2. Decision Framework: Which Option Should You Choose?</h2>
      <div className="space-y-4 my-6">
        <div className="border border-green-300 bg-green-50/10 p-5">
          <strong className="text-green-700 block text-base mb-2">Choose Rechecking (Rs 750) When:</strong>
          <ul className="text-sm space-y-1">
            <li>You answered all questions but your total marks seem lower than expected by a small margin (5-10 marks).</li>
            <li>You suspect a clerical totalling error (examiner may have missed adding marks for one section).</li>
            <li>You want a quick, low-cost check before deciding on full revaluation.</li>
          </ul>
        </div>
        <div className="border border-yellow-300 bg-yellow-50/10 p-5">
          <strong className="text-yellow-700 block text-base mb-2">Choose Revaluation (Rs 1,000) When:</strong>
          <ul className="text-sm space-y-1">
            <li>You are confident you wrote well but received significantly lower marks than expected (15+ marks below expectation).</li>
            <li>You need to convert a C grade to B+ or B+ to A to maintain CGPA for scholarship eligibility or postgraduate admission.</li>
            <li>You are borderline pass/fail (40-45% range) and a fresh evaluation could push you above the passing threshold.</li>
            <li>You have already obtained a photocopy and identified specific answers that appear to be underscored.</li>
          </ul>
        </div>
        <div className="border border-blue-300 bg-blue-50/10 p-5">
          <strong className="text-blue-700 block text-base mb-2">Choose Photocopy Inspection (Rs 500) When:</strong>
          <ul className="text-sm space-y-1">
            <li>You want to see exactly how the examiner marked each answer before deciding whether to invest Rs 1,000 in revaluation.</li>
            <li>You want to use the evaluated copy as a learning tool to understand where you lost marks.</li>
            <li>You are considering filing a formal grievance with the University Exam Committee and need documentary evidence.</li>
          </ul>
        </div>
      </div>

      {/* ─── Section 3: Step-by-Step Online Application ─── */}
      <h2>3. Step-by-Step Online Application Process</h2>
      <ol className="list-decimal list-inside space-y-4 text-sm my-6">
        <li>
          <strong>Log in to the DU Examination Portal:</strong> Visit{" "}
          <a href="https://exam.du.ac.in/" target="_blank" rel="noopener noreferrer">exam.du.ac.in</a>{" "}
          and log in with your student ID credentials (same as used for admit card download).
        </li>
        <li>
          <strong>Navigate to "Revaluation / Rechecking Application 2026":</strong> Under the Candidate Services tab, locate the active revaluation/rechecking link for the current examination cycle. This link is only active during the 15-day application window.
        </li>
        <li>
          <strong>Select Papers for Review:</strong> Choose the specific paper(s) you want to apply for. You can apply for rechecking and revaluation for different papers simultaneously (e.g., Rechecking for Macro I and Revaluation for Micro II).
        </li>
        <li>
          <strong>Upload Supporting Documents:</strong> Scan and upload:
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>Original Statement of Marks (downloaded from exam portal)</li>
            <li>College-verified identity proof</li>
            <li>Admit card for the relevant examination</li>
          </ul>
        </li>
        <li>
          <strong>Pay Online Fee:</strong> Use the integrated payment gateway (Debit Card / Credit Card / Net Banking / UPI). Save the transaction receipt PDF.
        </li>
        <li>
          <strong>Download Application Receipt:</strong> After successful payment, download and print the application receipt.
        </li>
        <li>
          <strong>Submit Hard Copy to College Office:</strong> Within 3 working days of online submission, submit the printed receipt along with a photocopy of your admit card to your college administration office. The college forwards applications to the DU Exam Branch in batches.
        </li>
        <li>
          <strong>Track Application Status:</strong> Check the exam portal periodically for status updates. Results are published online and communicated via registered email.
        </li>
      </ol>

      {/* ─── Section 4: Revaluation Success Rates ─── */}
      <h2>4. Revaluation Success Rates &amp; Mark Change Patterns</h2>
      <p>Based on historical patterns observed across DU colleges:</p>
      <ul>
        <li><strong>Rechecking:</strong> Approximately 5-8% of rechecking applications result in a mark change (typically 2-5 marks upward due to totalling errors).</li>
        <li><strong>Revaluation:</strong> Approximately 25-35% of revaluation applications result in a meaningful mark change (5 or more marks upward). In some cases, mark increases of 15-25 marks have been reported, particularly in descriptive-format papers.</li>
        <li><strong>Grade Changes:</strong> About 10-15% of revaluation applicants experience a letter grade change (e.g., B to B+ or C to B).</li>
      </ul>

      {/* ─── Section 5: Moderation Committee ─── */}
      <h2>5. Understanding the DU Moderation Committee</h2>
      <p>
        When the original examiner's marks and the revaluation examiner's marks differ by more than 15% of the maximum marks, the answer script is sent to a third examiner called the <strong>Moderation Committee Head Examiner</strong>. The Moderation Committee makes the final determination.
      </p>
      <div className="my-6 border border-rule bg-card p-5 text-sm font-mono">
        <strong className="mono-label text-grade block text-base mb-2">Moderation Decision Rules</strong>
        <ul className="space-y-2">
          <li><strong>If Difference is Less Than 15%:</strong> Higher of the two marks is automatically retained. No third evaluation needed.</li>
          <li><strong>If Difference is 15% or More:</strong> Third examiner evaluates independently. The average of the two closest marks (out of three) is taken as the final mark.</li>
          <li><strong>Floor Guarantee:</strong> In all cases, the final mark after revaluation cannot be lower than the original mark. This is a DU regulatory guarantee.</li>
        </ul>
      </div>

      {/* ─── Section 6: Common Mistakes ─── */}
      <h2>6. Five Common Mistakes in the Revaluation Process</h2>
      <div className="space-y-3 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Mistake 1: Missing the 15-Day Window</strong>
          <p className="text-sm">The revaluation/rechecking application link on exam.du.ac.in is deactivated exactly 15 days after result publication. There is no extension or late application facility. Set a reminder the day results are declared.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Mistake 2: Applying for Rechecking When Revaluation is Needed</strong>
          <p className="text-sm">If you believe answers were genuinely underscored (not just a totalling error), rechecking will not help you — it only verifies addition. Spend the extra Rs 250 and apply for full revaluation.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Mistake 3: Not Submitting the Hard Copy to College</strong>
          <p className="text-sm">Online payment alone does not complete the process. You must submit the printed receipt to your college office within 3 working days. Without this step, your application is not forwarded to the Exam Branch.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Mistake 4: Not Getting a Photocopy First</strong>
          <p className="text-sm">For Rs 500, you can see exactly what the examiner marked. This lets you make an informed decision about whether revaluation (Rs 1,000) is worth it. Consider getting photocopy first, especially for papers where you performed unexpectedly poorly.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Mistake 5: Ignoring Internal Assessment Impact</strong>
          <p className="text-sm">Revaluation only affects end-semester examination marks (out of 90). If your total marks are low due to poor internal assessment (IA) marks, revaluation cannot fix that. Check your IA component separately using the IAMC grievance process.</p>
        </div>
      </div>

      {/* ─── Section 7: Related Guides ─── */}
      <h2>7. Related College &amp; Exam Guides</h2>
      <ul>
        <li><a href="/blog/du-grading-attendance-explained" className="font-semibold underline text-grade">DU Grading and Attendance Rules</a></li>
        <li><a href="/blog/du-internal-assessment-grace-marks-guidelines-2026" className="font-semibold underline text-grade">DU Internal Assessment Grievance Process 2026</a></li>
        <li><a href="/blog/where-students-lose-marks-numericals" className="font-semibold underline text-grade">Where DU Students Lose Marks in Numericals</a></li>
        <li><a href="/blog/how-to-calculate-sgpa-to-cgpa-in-du-2026" className="font-semibold underline text-grade">How to Calculate SGPA to CGPA in DU 2026</a></li>
      </ul>
    </>
  );
}


